import React, { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Edit, FastForward, Visibility } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import axios from 'axios';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CustomerForm from 'src/components/ClientsCRUD';
import ClientDetailsModal from 'src/components/ClientsTable/ClientDetailsModal';
import ClientEditModal from 'src/components/ClientsTable/ClientEditModal';
import QuoteEdit from 'src/components/Quote/QuoteEdit';
import QuoteView from 'src/components/Quote/QuoteView';

const Commerce = () => {
  const { t } = useTranslation('registry');
  const { t: q } = useTranslation('config');
  const [customers, setCustomers] = useState();
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedCustomerDetails, setselectedCustomerDetails] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  const [isQuoteEditModalOpen, setIsQuoteEditModalOpen] = useState(false);
  const [selectedLicenseId, setSelectedLicenseId] = useState(null);
  const [isQuoteViewModalOpen, setIsQuoteViewModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [isCreateLicenseModalOpen, setIsCreateLicenseModalOpen] = useState(false);
  const [isCreatingLicense, setIsCreatingLicense] = useState(false);
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [isSendCupModalOpen, setIsSendCupModalOpen] = useState(false);
  const [isSendingCup, setIsSendingCup] = useState(false);
  const [dataBridges, setDataBridges] = useState([]); // Valor inicial vacío
  const [selectedBridges, setSelectedBridges] = useState([]);
  const [isAssigningBridges, setIsAssigningBridges] = useState(false);
  const [license, setLicense] = useState(undefined);
  const { accounts } = useMsal();

  const closeQuoteViewModal = () => {
    setIsQuoteViewModalOpen(false);
    setSelectedQuote(null);
  };

  const openQuoteEditModal = (customer, quote) => {
    setselectedCustomerDetails(customer || customers.find(c => c.customer_id === quote?.customerId));
    setIsQuoteEditModalOpen(true);
  };

  const closeCreateLicenseModal = () => {
    setselectedCustomerDetails(null);
    setIsCreateLicenseModalOpen(false);
  };

  const openCreateLicenseModal = customer => {
    setselectedCustomerDetails(customer);
    setIsCreateLicenseModalOpen(true);
  };

  const closeSendCupModal = () => {
    setselectedCustomerDetails(null);
    setIsSendCupModalOpen(false);
  };

  const openSendCupModal = customer => {
    setselectedCustomerDetails(customer);
    setIsSendCupModalOpen(true);
  };

  const theme = useTheme(); // Usar el tema actual para aplicar estilos dinámicamente

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/sales/customers`);
      console.log(response);
      setCustomers(response.data);
      setFilteredCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, []);

  const fetchCustomerDetails = async customerId => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/sales/customer/${customerId}`);
      console.log('Customer details:', response.data);
      setLicense(response.data.licenses);
      setselectedCustomerDetails(response.data);
    } catch (error) {
      console.error('Error fetching customer details:', error);
      setselectedCustomerDetails(null);
    }
  };

  const fetchSomeQuote = async customerId => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/sales/quote/last/${customerId}`);
      return response; // Devuelve la respuesta para su uso en `handleViewDetails`
    } catch (error) {
      console.error('Error fetching some quote:', error);
      return null; // Manejo de errores básico
    }
  };

  const generateLicense = async () => {
    const customer = selectedCustomerDetails;
    const quoteId = customer?.quotes?.[0]?.quote_id;

    setIsCreatingLicense(true);
    try {
      console.log('Generanting license for quoteId:', quoteId);
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/sales/license`, {
        customer_id: customer.customer_id,
        quote_id: quoteId
      });

      if (res.status === 200) {
        console.log('License generated successfully');
        toast.success(q('quote.quoteView.textLicense'));
      } else {
        throw new Error('Error generating license');
      }
    } catch (error) {
      console.error('Error generating license:', error);
    } finally {
      await fetchCustomers();
      setIsCreatingLicense(false);
      setIsCreateLicenseModalOpen(false);
    }
  };

  const getInPersonVisit = customer => {
    if (customer.stage !== 'prospect') {
      return t('commerce.status.visit.completed');
    }
    return t('commerce.status.visit.pending');
  };

  const getNextButtonStyle = customer => {
    const cupConsumed = customer.licenses?.[0]?.cup_details?.consumed;
    const cupSent = customer.licenses?.[0]?.cup_details?.sent_to_email;
    const hasLicense = customer.licenses?.length;

    const isActiveCustomer = hasLicense && (cupConsumed || cupSent);
    const isProspectStageWithoutVisit = customer.stage === 'prospect';
    const isBridgeAssigned = customer.stage === 'bridge-assigned';

    const cursor = isActiveCustomer || isProspectStageWithoutVisit || isBridgeAssigned ? 'default' : 'pointer';
    const opacity = isActiveCustomer || isProspectStageWithoutVisit || isBridgeAssigned ? 0.5 : 1;

    return { cursor, opacity };
  };

  const getPlan = customer => {
    if (customer.stage === 'prospect') {
      return '-';
    }
    if (customer.quotes?.[0]?.plan) {
      return (
        <>
          {customer.quotes[0].plan.name}
          <br />
          {t('commerce.numberOfUsers')}: {customer.quotes[0].number_of_users}
        </>
      );
    }
    return 'N/A';
  };

  const getStatus = customer => {
    const cupConsumed = customer.licenses?.[0]?.cup_details?.consumed;

    if (customer.stage === 'prospect' || customer.stage === 'prospect-visit') {
      return t('commerce.prospect');
    } else if (customer.stage === 'quote-created') {
      return t('commerce.status.quote.created');
    } else if (customer.stage === 'quote-sent') {
      return t('commerce.status.quote.sent');
    } else if (customer.stage === 'license-created') {
      return t('commerce.status.license.created');
    } else if (customer.stage === 'bridge-assigned') {
      return t('commerce.status.bridge.assigned');
    } else if (customer.stage === 'bridge-configured') {
      return t('commerce.status.bridge.configured');
    } else if (customer.stage === 'cup-sent') {
      return t('commerce.status.cup.sent');
    } else if (customer.stage === 'customer' || cupConsumed) {
      return t('commerce.status.cup.active');
    }
    return 'N/A';
  };

  const handleNextButtonClick = async customer => {
    setselectedCustomerDetails(customer);
    try {
      if (customer.stage === 'prospect-visit') {
        openQuoteEditModal(customer);
      } else if (customer.stage === 'quote-created') {
        await handleViewQuote(customer.customer_id);
      } else if (customer.stage === 'quote-sent') {
        openCreateLicenseModal(customer);
      } else if (customer.stage === 'license-created') {
        handleViewLicense(customer);
      } else if (customer.stage === 'bridge-configured') {
        openSendCupModal(customer);
      }
    } catch (error) {
      console.error('Error in handleNextButtonClick:', error);
    }
    return null;
  };

  const handleViewQuote = async customerId => {
    try {
      const response = await fetchSomeQuote(customerId);
      if (response?.data?.length > 0) {
        const quote = response.data[0];

        // Formatea los datos de la cotización
        const formattedQuote = {
          bridge: quote.number_of_bridges,
          businessPartner: {
            email: quote.businessPartnerEmail,
            name: quote.businessPartnerName
          },
          customerId: quote.customer_id,
          id: quote.quote_id,
          numberOfAccessPoints: quote.number_of_access_points,
          numberOfUsers: quote.number_of_users,
          planId: quote.plan_id,
          planName: quote.plan_name,
          pricePerUser: quote.price_per_user,
          propertyName: quote.nameQuoteCustomer,
          status: quote.status,
          totalPrice: quote.total_price
        };

        setSelectedQuote(formattedQuote);
        setIsQuoteViewModalOpen(true);
      } else {
        console.warn('No quote available for this customer.');
      }
    } catch (error) {
      console.error('Error fetching the quote:', error);
    }
  };

  useEffect(() => {
    if (!customers) fetchCustomers();
  }, [customers, fetchCustomers]);

  const handleSearch = e => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    if (value) {
      const filtered = customers.filter(
        customer =>
          customer.name.toLowerCase().includes(value) ||
          customer.bc_email.toLowerCase().includes(value) ||
          `${customer.bc_first_name} ${customer.bc_last_name}`.toLowerCase().includes(value)
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  };

  const handleViewDetails = async customer => {
    try {
      // Check if quote exists for the customer
      const response = await fetchSomeQuote(customer.customer_id);
      if (response?.data?.length > 0) {
        console.log('Quote found:', response.data[0]);
        console.log(response.data[0].status === 'sent');
        await fetchCustomerDetails(customer.customer_id);
        setIsDetailModalOpen(true);
      } else {
        await fetchCustomerDetails(customer.customer_id);
        setIsDetailModalOpen(true);
      }
    } catch (error) {
      console.error('Error al verificar cotizaciones:', error);
    }
  };

  //Funciones para manejar la asignación de Bridges a la licencia
  // Función para abrir el modal y cargar los datos
  const handleViewLicense = async customer => {
    const licenseId = customer?.licenses?.[0]?.license_id;
    setSelectedLicenseId(licenseId);
    try {
      const responseBridge = await axios.get(`${process.env.REACT_APP_API_URL}/sales/bridges`);

      const bridges = responseBridge.data.filter(bridge => bridge.license_id === null);
      setDataBridges(bridges);
      setIsLicenseModalOpen(true);
    } catch (error) {
      console.error('Error fetching license details:', error);
    }
  };

  // Función para manejar selección de Bridges
  const handleBridgeSelection = bridgeId => {
    setSelectedBridges(prev => (prev.includes(bridgeId) ? prev.filter(id => id !== bridgeId) : [...prev, bridgeId]));
  };

  // Función para enviar Bridges seleccionados
  const handleAssignBridges = async () => {
    console.log('Bridges seleccionados:', selectedBridges);

    if (selectedBridges.length === 0) return;

    if (selectedBridges.length !== selectedCustomerDetails.quotes[0].number_of_bridges) {
      return toast.error(t('commerce.modal.assignBridgeMismatch'));
    }

    setIsAssigningBridges(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/sales/bridge/assign`, {
        bridge_ids: selectedBridges,
        license_id: selectedLicenseId || license?.[0]?.license_id,
        updated_by: accounts?.[0]?.idTokenClaims?.sub
      });
      setIsLicenseModalOpen(false);
      setSelectedLicenseId(null);
      setSelectedBridges([]);
      await fetchCustomers();
      toast.success(t('commerce.modal.assignSuccess'));
    } catch (error) {
      console.error('Error asignando bridges:', error);
    } finally {
      setIsAssigningBridges(false);
    }
  };

  const handleEditCustomer = async customer => {
    console.log('Opening ClientEditModal for customer:', customer.customer_id);
    try {
      await fetchCustomerDetails(customer.customer_id);
      console.log('Fetched details, opening modal');
      setIsFormModalOpen(false); // Ensure legacy form is closed
      setIsEditModalOpen(true);
    } catch (e) {
      console.error('Error in handleEditCustomer', e);
    }
  };

  const handleNewCustomer = () => {
    setselectedCustomerDetails(null);
    setIsFormModalOpen(true);
  };

  const formatDate = date => (date ? format(new Date(date), 'dd/MM/yyyy HH:mm') : t('commerce.modal.noData'));

  const sendCUP = useCallback(
    async () => {
      const licenseId = selectedCustomerDetails?.licenses?.[0]?.license_id;
      console.log('Sending CUP for licenseId:', licenseId);
      console.log('Sending CUP with account:', accounts?.[0]?.idTokenClaims?.sub);

      setIsSendingCup(true);
      try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/sales/send-cup`, {
          licenseID: licenseId,
          sentBy: accounts?.[0]?.idTokenClaims?.sub
        });

        console.log('res:', res);
        if (res.status === 200) {
          await fetchCustomers();
          toast.success(q('cup.cupSent'));
        }
      } catch (error) {
        console.error('Error sending CUP:', error);
        toast.error(q('cup.sendCupErrorText'));
      } finally {
        setIsSendCupModalOpen(false);
        setIsSendingCup(false);
      }
    },
    [accounts, fetchCustomers, q, selectedCustomerDetails] // Dependencies
  );

  return (
    <>
      <Box style={{ display: 'flex', gap: 20, justifyContent: 'end', marginBottom: 15 }}>
        <Button color="primary" onClick={handleNewCustomer} variant="contained">
          {t('commerce.addClient')}
        </Button>
        <TextField
          onChange={handleSearch}
          placeholder={t('commerce.searchPlaceholder')}
          style={{ maxWidth: 300 }}
          value={searchText}
          variant="outlined"
        />
      </Box>
      <TableContainer
        component={Paper}
        style={{
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          margin: 'auto',
          marginTop: 20
        }}
      >
        <Table aria-label="Client Table">
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold' }}>{t('commerce.clientName')}</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>{t('commerce.status.label')}</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>{t('commerce.businessContact')}</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>{t('commerce.inPersonVisit')}</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>{t('commerce.plan')}</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>{t('commerce.creationDate')}</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>{t('commerce.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCustomers?.map((customer, index) => (
              <TableRow key={customer.customer_id || index}>
                <TableCell>{customer.name}</TableCell>
                <TableCell>{getStatus(customer)}</TableCell>
                <TableCell>
                  {`${customer.bc_first_name} ${customer.bc_last_name}`}
                  <br />
                  {customer.bc_email}
                </TableCell>
                <TableCell>{getInPersonVisit(customer)}</TableCell>
                <TableCell>{getPlan(customer)}</TableCell>
                <TableCell>{formatDate(customer.created_at)}</TableCell>
                <TableCell>
                  <Box style={{ display: 'flex', gap: '10px' }}>
                    <FastForward onClick={() => handleNextButtonClick(customer)} style={getNextButtonStyle(customer)} />
                    <Visibility onClick={() => handleViewDetails(customer)} style={{ cursor: 'pointer' }} />
                    <Edit onClick={() => handleEditCustomer(customer)} style={{ cursor: 'pointer' }} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ClientDetailsModal client={selectedCustomerDetails} isOpen={isDetailModalOpen} onClose={() => setIsDetailModalOpen(false)} />
      <ClientEditModal
        client={selectedCustomerDetails}
        fetchCustomers={fetchCustomers}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />
      {/* Modal para Formulario prospecto */}
      <Modal onClose={() => setIsFormModalOpen(false)} open={isFormModalOpen}>
        <Box
          style={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: 8,
            boxShadow: theme.shadows[5],
            color: theme.palette.text.primary,
            left: '50%',
            maxWidth: 900,
            padding: 20,
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%'
          }}
        >
          <CustomerForm fetchCustomers={fetchCustomers} id={null} initialValues={null} isEditMode={false} onClose={() => setIsFormModalOpen(false)} />
        </Box>
      </Modal>
      {/* Modal para crear una cotizaciones */}
      <Modal
        aria-describedby="quote-edit-modal-description"
        aria-labelledby="quote-edit-modal"
        onClose={() => setIsQuoteEditModalOpen(false)}
        open={isQuoteEditModalOpen}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            left: '50%',
            maxWidth: '900px',
            p: 4,
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%'
          }}
        >
          <QuoteEdit
            fetchCustomers={fetchCustomers}
            onClose={() => setIsQuoteEditModalOpen(false)} // Cierra el modal directamente
            onSave={updatedQuote => {
              console.log('Cotización actualizada:', updatedQuote);
              setIsQuoteEditModalOpen(false);
              setIsQuoteViewModalOpen(false);
              setIsDetailModalOpen(false);
            }}
            quote={{ ...selectedQuote, ...selectedCustomerDetails }}
          />
        </Box>
      </Modal>
      {/* Modal para ver una Quote ya creada desde prospecto */}
      <Modal
        aria-describedby="quote-view-modal-description"
        aria-labelledby="quote-view-modal"
        onClose={closeQuoteViewModal}
        open={isQuoteViewModalOpen}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            left: '50%',
            maxWidth: '900px',
            p: 4,
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%'
          }}
        >
          <QuoteView
            fetchCustomers={fetchCustomers}
            onEdit={updatedQuote => openQuoteEditModal(undefined, updatedQuote)}
            onSave={closeQuoteViewModal}
            quote={selectedQuote}
          />
        </Box>
      </Modal>
      {/* Modal para asignar Bridges a la licencia */}
      <Modal onClose={() => setIsLicenseModalOpen(false)} open={isLicenseModalOpen}>
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            left: '50%',
            maxHeight: '90vh',
            overflowY: 'scroll',
            p: 4,
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '60%'
          }}
        >
          <Typography sx={{ mb: 2 }} variant="h6">
            {t('commerce.modal.assignBridge')}
          </Typography>
          <Typography sx={{ mb: 2 }} variant="body2">
            {t('commerce.modal.assignBridgeText')}
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{t('commerce.modal.numberSerie')}</TableCell>
                  <TableCell>{t('commerce.modal.creationDate')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataBridges && dataBridges.length > 0 ? (
                  dataBridges.map(bridge => (
                    <TableRow key={bridge.bridge_id}>
                      <TableCell>
                        <input
                          checked={selectedBridges.includes(bridge.bridge_id)}
                          onChange={() => handleBridgeSelection(bridge.bridge_id)}
                          type="checkbox"
                        />
                      </TableCell>
                      <TableCell>{bridge.serial_number}</TableCell>
                      <TableCell>{format(new Date(bridge.created_at), 'dd MMM yyyy HH:mm')}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={3}>
                      {t('commerce.modal.notBridgesData')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={() => setIsLicenseModalOpen(false)} sx={{ mr: 2 }}>
              {t('commerce.modal.close')}
            </Button>
            <Button color="primary" disabled={isAssigningBridges || !selectedBridges?.length} onClick={handleAssignBridges} variant="contained">
              {t(isAssigningBridges ? 'commerce.modal.assigningBridges' : 'commerce.modal.assignBridge')}
            </Button>
          </Box>
        </Box>
      </Modal>
      {/* License creation modal*/}
      <Dialog onClose={closeCreateLicenseModal} open={isCreateLicenseModalOpen}>
        <DialogTitle>{q('quote.quoteView.generateLincense')}</DialogTitle>
        <DialogContent>
          <Typography>{q('quote.quoteView.confirmLicense')}.</Typography>
          <Typography>
            QuoteId de la cotización: <strong>{selectedCustomerDetails?.quotes?.[0]?.quote_id}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={closeCreateLicenseModal}>
            {q('quote.quoteView.cancel')}
          </Button>
          <Button color="primary" disabled={isCreatingLicense} onClick={generateLicense} variant="contained">
            {q(isCreatingLicense ? 'quote.quoteView.generatingLicense' : 'quote.quoteView.generateLincense')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Send CUP Modal */}
      <Dialog onClose={closeSendCupModal} open={isSendCupModalOpen}>
        <DialogTitle>{q('cup.confirmSendCupTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{q('cup.confirmSendCupText')}.</Typography>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={closeSendCupModal}>
            {q('cup.cancel')}
          </Button>
          <Button color="primary" disabled={isSendingCup} onClick={sendCUP} variant="contained">
            {q(isSendingCup ? 'cup.sendingCup' : 'cup.sendCup')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Commerce;
